import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { useApiServices } from '@shared/lib/use-api-services';
import { swrKeys } from '@shared/api/swr/keys';
import { getApiErrorMessage } from '@shared/lib/get-api-error-message';
import { readApiError } from '@shared/api/errors/read-api-error';
import { useAuth } from '@entities/auth';
import {
  isAutoWithdrawalActionResponse,
  type AutoWithdrawalAction,
  type AutoWithdrawalDto,
  type AutoWithdrawalPayload,
} from '@shared/api/contracts';
import {
  getAutoWithdrawalSettingsStatus,
  type AutoWithdrawalSettingsStatus,
} from '@entities/withdrawal';

export type SaveAutoWithdrawalResult =
  | { success: true; status: AutoWithdrawalSettingsStatus }
  | {
      success: false;
      action?: AutoWithdrawalAction;
      error?: string;
      invalidCode?: boolean;
      notFound?: boolean;
    };

export const useAutoWithdrawal = () => {
  const { t } = useTranslation('cabinet');
  const services = useApiServices();
  const { refreshUser } = useAuth();
  const { mutate } = useSWR(swrKeys.accounting.balance());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const saveAutoWithdrawal = async (
    payload: AutoWithdrawalPayload,
    existing: AutoWithdrawalDto | null,
  ): Promise<SaveAutoWithdrawalResult> => {
    setIsLoading(true);
    setError(null);

    try {
      const apiCall = existing
        ? services.withdrawals.updateAutoWithdrawal
        : services.withdrawals.createAutoWithdrawal;

      const response = await apiCall(payload);

      if (isAutoWithdrawalActionResponse(response)) {
        const message =
          response.action === 'register_otp'
            ? t('withdrawal.auto.modal.need2faSetup')
            : t('withdrawal.auto.modal.need2faCode');
        setError(message);
        return {
          success: false,
          action: response.action,
          error: message,
          invalidCode: response.action === 'confirm_otp',
        };
      }

      const freshBalance = await mutate();
      const status = getAutoWithdrawalSettingsStatus(
        (freshBalance as { auto_withdraw?: AutoWithdrawalDto | null } | undefined)?.auto_withdraw ??
          response,
      );

      return { success: true, status };
    } catch (err: unknown) {
      const statusCode = (err as { statusCode?: number })?.statusCode;
      if (statusCode === 404 && existing) {
        await mutate();
        return {
          success: false,
          notFound: true,
          error: t('withdrawal.auto.modal.error'),
        };
      }

      const parsed = readApiError(err);
      if (parsed?.code === 'OTP_REQUIRED' || parsed?.code === 'OTP_INVALID') {
        throw err;
      }
      if (parsed?.code === 'WITHDRAWALS_LOCKED') {
        await refreshUser();
      }

      const message = getApiErrorMessage(err, t, 'withdrawal.auto.modal.error');
      setError(message);

      const invalidCode =
        statusCode === 400 &&
        typeof message === 'string' &&
        /2fa|код|code/i.test(message);

      return {
        success: false,
        error: message,
        invalidCode,
      };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    saveAutoWithdrawal,
    isLoading,
    error,
  };
};
